import "./InvestmentDetails.css";

import { RoundedPlusSignIcon, SkipForwardIcon } from "../../../../../../assets";
import { DealSpecificProperty, InvestmentDetailsInterface, SelectData } from "../../../../../../models";
import {
  Button,
  Column,
  FullPageCard,
  KanaTooltip,
  MultiSelect,
  Row,
  Select,
  TextArea,
} from "../../../../../../widget";
import { DynamicInvestmentDetails } from "./components/DynamicInvestmentDetails/DynamicInvestmentDetails";
import { useInvestmentDetails } from "./useInvestmentDetails";

export interface InvestmentDetailsProps {
  selectedInvestment: InvestmentDetailsInterface | undefined;
  hasInvestments: boolean;
  dealSpecificSchemaProperties: [string, DealSpecificProperty][] | undefined;
  instrumentSelectData: SelectData;
  associationSelectData: SelectData;
  hasValidationError: (investmentKey: string | undefined, fieldKey: string) => boolean;
  onAddInvestment: () => void;
  onUpdateInvestment: <K extends keyof InvestmentDetailsInterface>(
    key: string,
    field: K,
    value:
      | InvestmentDetailsInterface[K]
      | ((currentValue: InvestmentDetailsInterface[K]) => InvestmentDetailsInterface[K])
  ) => void;
}

export const InvestmentDetails = ({
  selectedInvestment,
  hasInvestments,
  dealSpecificSchemaProperties,
  instrumentSelectData,
  associationSelectData,
  hasValidationError,
  onAddInvestment,
  onUpdateInvestment,
}: InvestmentDetailsProps): JSX.Element => {
  const { handleValidateAndNext } = useInvestmentDetails();

  return (
    <div className="InvestmentDetailsCard">
      <FullPageCard corners="rounded" padding="ll" paddingBottom="s">
        <div className="InvestmentDetailsContainer">
          <Row spacingV="ml">
            <Column span={12}>
              <h2>Investment details</h2>
            </Column>
          </Row>
          {!hasInvestments ? (
            <>
              <Row spacingV="l">
                <Column span={12}>
                  <p className="body1 InvestmentDetailsIntro">
                    Add an investment to begin. You can create multiple investments and assign each to different
                    projects or organisations. Investments are used to define how capital is deployed within this deal.
                  </p>
                </Column>
              </Row>
              <Row>
                <Column span={12}>
                  <div className="InvestmentDetailsActions">
                    <Button
                      text="Add investment"
                      icon={<RoundedPlusSignIcon className="InvestmentDetailsPlusIcon" />}
                      iconPosition="left"
                      onClick={onAddInvestment}
                      size="smallLarge"
                    />
                    <Button
                      text="Skip step"
                      variant="tertiary"
                      icon={<SkipForwardIcon className="InvestmentDetailsSkipIcon" />}
                      onClick={() => handleValidateAndNext(false)}
                      size="smallLarge"
                    />
                  </div>
                </Column>
              </Row>
            </>
          ) : (
            <div className="InvestmentDetailsForm">
              <Row spacingV="ml">
                <Column span={6}>
                  <Select
                    label="Instrument"
                    placeholder="Select"
                    data={instrumentSelectData}
                    value={selectedInvestment?.instrument}
                    hasError={hasValidationError(selectedInvestment?.key, "instrument")}
                    onChange={(value) => {
                      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
                      onUpdateInvestment(selectedInvestment!.key, "instrument", value);
                      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
                      onUpdateInvestment(selectedInvestment!.key, "fields", {});
                    }}
                  />
                </Column>
                <Column span={6}>
                  <MultiSelect
                    label="Associations"
                    placeholder="Select"
                    tooltip={
                      <KanaTooltip
                        tooltipHeader="Associations"
                        tooltipText="Choose the entities involved in this investment. For example, if this loan is being distributed between a company and its projects, make sure to select all relevant options here."
                      />
                    }
                    data={associationSelectData}
                    values={selectedInvestment?.associations}
                    hasError={hasValidationError(selectedInvestment?.key, "associations")}
                    // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
                    onChange={(value) => onUpdateInvestment(selectedInvestment!.key, "associations", value)}
                  />
                </Column>
              </Row>
              <Row spacingV={dealSpecificSchemaProperties ? "l" : undefined}>
                <Column span={12} className="InvestmentDetailsDescription">
                  <TextArea
                    label="Description"
                    name="investmentDescription"
                    placeholder="Enter a description..."
                    value={selectedInvestment?.description}
                    // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
                    onChange={(value) => onUpdateInvestment(selectedInvestment!.key, "description", value)}
                  />
                </Column>
              </Row>
              <DynamicInvestmentDetails
                selectedInvestment={selectedInvestment}
                dealSpecificSchemaProperties={dealSpecificSchemaProperties}
                hasFieldError={(fieldKey) => hasValidationError(selectedInvestment?.key, fieldKey)}
                onUpdateInvestment={onUpdateInvestment}
                isReadonly={false}
              />
            </div>
          )}
        </div>
      </FullPageCard>
    </div>
  );
};
